import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../hooks";
import {
  tenantFormValidationSchema,
  TenantFormValues,
} from "./validation/tenantFormValidation";
import {
  UpdateTenantDto,
  useGetTenantByIdQuery,
  useUpdateTenantMutation,
} from "../../api/tenantsApi";
import { TenantFormContent } from "./components/TenantFormContent";
import { Box, CircularProgress, Typography } from "@mui/material";

const TenantEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tenantId = parseInt(id || "0");

  const { data: tenant, isLoading: isFetchingTenant } = useGetTenantByIdQuery(tenantId, {
    skip: !tenantId,
  });
  const [updateTenant] = useUpdateTenantMutation();

  const formik = useFormik<TenantFormValues>({
    initialValues: {
      name: "",
      contactEmail: "",
      contactPhone: "",
      safetyEmail: "",
      isActive: true,
    },
    validationSchema: tenantFormValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(errors).reduce((acc: any, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          true
        );
        return;
      }

      await handleSubmit(async () => {
        const updateTenantDto: UpdateTenantDto = {
          name: values.name,
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          safetyEmail: values.safetyEmail || undefined,
          isActive: values.isActive,
        };

        return await updateTenant({ id: tenantId, tenant: updateTenantDto }).unwrap();
      });
    },
  });

  // Populate form when tenant data is loaded
  useEffect(() => {
    if (tenant) {
      formik.setValues({
        name: tenant.name,
        contactEmail: tenant.contactEmail || "",
        contactPhone: tenant.contactPhone || "",
        safetyEmail: tenant.safetyEmail || "",
        isActive: tenant.isActive,
      });
    }
  }, [tenant]);

  const completionPercentage = useFormCompletion(formik.values);
  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        navigate("/tenants");
      }, 1500);
    },
    successMessage: "Tenant updated successfully!",
  });

  const handleBack = () => {
    navigate("/tenants");
  };

  // Loading state while fetching tenant
  if (isFetchingTenant) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading tenant...
        </Typography>
      </Box>
    );
  }

  // Error state if tenant not found
  if (!tenant && !isFetchingTenant) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          Tenant not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The tenant you are looking for does not exist or has been deleted.
        </Typography>
      </Box>
    );
  }

  return (
    <BaseFormPage
      title="Edit Tenant"
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText="Update Tenant"
    >
      <TenantFormContent mode="edit" isLoading={isSubmitting} tenant={tenant} />
    </BaseFormPage>
  );
};

export default TenantEditPage;

